import { useState } from 'react';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import {
    useBackOfficeProjects,
    useCreateBackOfficeProject,
    useDeleteBackOfficeProjectPermanently,
    useSetBackOfficeProjectDeleted,
    type BackOfficeProject,
} from '@/services/projectBackOffice.service';
import { saveErrorMessage } from './saveError';
import { DEFAULT_PROJECT_ID } from '@/lib/defaultProject';

const FIELD =
    'bg-[#0f141a] border-[#1e242a] text-[#e5e7e3] placeholder:text-[#6b7480]';

export function DeletedTag() {
    return (
        <span className="inline-block rounded bg-red-900/30 px-2 py-0.5 text-[11px] font-medium text-red-300">
            Deleted
        </span>
    );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <label className="block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5">
            {children}
        </label>
    );
}

function CreateProjectForm() {
    const { mutate: create, isPending, error } = useCreateBackOfficeProject();
    const [name, setName] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');

    const handleCreate = () => {
        if (!name.trim()) return;
        create(
            {
                name: name.trim(),
                description: description.trim() || undefined,
                internalTeamOnlyLabel: label.trim() || undefined,
            },
            {
                onSuccess: () => {
                    setName('');
                    setLabel('');
                    setDescription('');
                },
            },
        );
    };

    return (
        <DataCard>
            <div className="p-5 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_14rem_1.5fr_auto] gap-3 items-end">
                    <div>
                        <FieldLabel>Name</FieldLabel>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="As its members will see it"
                            className={FIELD}
                        />
                    </div>
                    <div>
                        <FieldLabel>Internal label</FieldLabel>
                        <Input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Humeos only"
                            className={FIELD}
                        />
                    </div>
                    <div>
                        <FieldLabel>Description</FieldLabel>
                        <Input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional"
                            className={FIELD}
                        />
                    </div>
                    <Button
                        onClick={handleCreate}
                        disabled={isPending || !name.trim()}
                        className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                    >
                        {isPending ? (
                            <Loader2Icon className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                            <PlusIcon className="h-4 w-4 mr-1.5" />
                        )}
                        Create project
                    </Button>
                </div>
                {error && (
                    <div className="text-sm text-red-400">
                        {saveErrorMessage(error)}
                    </div>
                )}
                <p className="text-xs text-[#6b7480]">
                    You become its owner, hidden from its members. Add the
                    customer from the project&apos;s settings.
                </p>
            </div>
        </DataCard>
    );
}

function ProjectActions({ project }: { project: BackOfficeProject }) {
    const setDeleted = useSetBackOfficeProjectDeleted();
    const removeForGood = useDeleteBackOfficeProjectPermanently();
    const busy = setDeleted.isPending || removeForGood.isPending;
    const error = setDeleted.error ?? removeForGood.error;

    const button =
        'rounded px-2 py-1 text-xs transition disabled:opacity-40 disabled:pointer-events-none';

    if (project.id === DEFAULT_PROJECT_ID)
        return (
            <span
                className="text-xs text-[#6b7480]"
                title="Unknown scanners land here when they first authenticate"
            >
                Unknown scanners
            </span>
        );

    return (
        <div className="flex flex-col items-end gap-1">
            <div className="flex justify-end gap-1.5">
                {project.softDelete ? (
                    <>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                                setDeleted.mutate({
                                    projectId: project.id,
                                    deleted: false,
                                })
                            }
                            className={`${button} text-[#74c69d] hover:bg-[#16271d]`}
                        >
                            Restore
                        </button>
                        <button
                            type="button"
                            disabled={busy || project.scannerCount > 0}
                            title={
                                project.scannerCount > 0
                                    ? 'Move its scanners to another project first'
                                    : undefined
                            }
                            onClick={() => {
                                const typed = window.prompt(
                                    `Delete "${project.name}" for good, with its ${project.sampleCount} sample(s) and ${project.imageCount} image record(s)? The S3 files stay.\n\nType the project's name to confirm.`,
                                );
                                if (typed?.trim() === project.name.trim())
                                    removeForGood.mutate(project.id);
                            }}
                            className={`${button} text-red-300 hover:bg-red-900/30`}
                        >
                            Delete for good
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                            if (
                                window.confirm(
                                    `Delete "${project.name}"? It disappears for its members; you can restore it.`,
                                )
                            )
                                setDeleted.mutate({
                                    projectId: project.id,
                                    deleted: true,
                                });
                        }}
                        className={`${button} text-[#a8b0bb] hover:bg-red-900/30 hover:text-red-300`}
                    >
                        Delete
                    </button>
                )}
            </div>
            {error && (
                <div className="max-w-xs text-right text-xs text-red-400">
                    {saveErrorMessage(error)}
                </div>
            )}
        </div>
    );
}

export default function BackOfficeProjects() {
    const { data, isLoading, error } = useBackOfficeProjects();
    const [query, setQuery] = useState('');

    const q = query.trim().toLowerCase();
    const rows = (data ?? []).filter(
        (p) =>
            !q ||
            p.name.toLowerCase().includes(q) ||
            (p.internalTeamOnlyLabel ?? '').toLowerCase().includes(q),
    );

    return (
        <BackofficePage title="Projects">
            <CreateProjectForm />

            <div className="mt-6 mb-4">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by name or internal label…"
                    className="w-full max-w-sm bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-2 text-sm text-[#e5e7e3] placeholder-[#6b7480] focus:outline-none focus:border-[#2d6a4f]"
                />
            </div>

            {isLoading ? (
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            ) : error ? (
                <div className="text-red-400 text-sm">{error.toString()}</div>
            ) : (
                <DataCard>
                    <DataTable>
                        <thead className="bg-[#0f141a]">
                            <tr>
                                <TH>Project</TH>
                                <TH align="right">Members</TH>
                                <TH align="right">Samples</TH>
                                <TH align="right">Scanners</TH>
                                <TH align="right">Images</TH>
                                <TH>Created</TH>
                                <TH align="right"> </TH>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => (
                                <TR
                                    key={p.id}
                                    className={p.softDelete ? 'opacity-60' : ''}
                                >
                                    <TD>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-white">
                                                {p.name}
                                            </span>
                                            {p.softDelete && <DeletedTag />}
                                        </div>
                                        {p.internalTeamOnlyLabel && (
                                            <div className="text-xs text-[#6b7480]">
                                                {p.internalTeamOnlyLabel}
                                            </div>
                                        )}
                                    </TD>
                                    <TD align="right">{p.memberCount}</TD>
                                    <TD align="right">{p.sampleCount}</TD>
                                    <TD align="right">{p.scannerCount}</TD>
                                    <TD align="right">{p.imageCount}</TD>
                                    <TD className="whitespace-nowrap text-[#a8b0bb]">
                                        {new Date(
                                            p.createdAt,
                                        ).toLocaleDateString()}
                                    </TD>
                                    <TD align="right">
                                        <ProjectActions project={p} />
                                    </TD>
                                </TR>
                            ))}
                        </tbody>
                    </DataTable>
                </DataCard>
            )}
        </BackofficePage>
    );
}
